/**
 * main-product-v5.js
 *
 * KEY FEATURE: Flavor selector uses actual product variants.
 * Clicking a flavor card:
 *   1. Marks it as selected
 *   2. Finds the matching variant from embedded product JSON
 *      (matches by flavor value; also considers audience option value
 *       if the audience block carries a data-option-value attribute)
 *   3. Updates all price displays + the hidden variant input
 *
 * Other handlers: audience selector, sub/OTP toggle,
 * FAQ accordion, nutritional label dialog, gift dialogs.
 */

(function () {
  var scriptTag = document.currentScript;
  var sectionId = scriptTag ? scriptTag.dataset.id : null;

  function getSection() {
    return sectionId ? document.getElementById(sectionId) : document;
  }

  /* ── Helpers ── */

  function fmtMoney(cents) {
    if (cents == null) return '';
    var val = cents / 100;
    return '$' + (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2));
  }

  // "$99" / "99.00" → 9900 (cents). Used to recompute the welcome-kit total
  // whenever the active plan (and therefore the blend's compare price) changes.
  function parseMoneyToCents(text) {
    var n = parseFloat((text || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function splitPlanText(value) {
    return (value || '').split('/').map(function (part) { return part.trim(); });
  }

  function setText(section, selector, value) {
    var el = section.querySelector(selector);
    if (el) el.textContent = value || '';
  }

  /**
   * The One-Time Purchase plan is ALWAYS the first buy group (the 1-month,
   * quantity: 1 one) regardless of which subscription plan is highlighted.
   * OTP is a fixed single-month purchase, so its price, supply text and cart
   * quantity must never follow the active plan.
   */
  function getOtpPlan(section) {
    return section.querySelector('.productV5GroupsItemInput');
  }

  function setAtcButtonText(section, actionText, priceText, isAvailable) {
    var atcTextEl = section.querySelector('.productV5AtcText');
    if (!atcTextEl) return;

    if (!isAvailable) {
      atcTextEl.textContent = 'Sold Out';
      return;
    }

    if (!atcTextEl.querySelector('.productV5AtcActionText')) {
      atcTextEl.innerHTML = '<span class="productV5AtcActionText"></span><span class="productV5AtcSeparator"> - </span><span class="productV5AtcPlanPrice"></span><span class="productV5AtcArrow" aria-hidden="true">→</span>';
    }

    setText(section, '.productV5AtcActionText', actionText);
    setText(section, '.productV5AtcPlanPrice', priceText);

    var separator = section.querySelector('.productV5AtcSeparator');
    if (separator) separator.style.display = priceText ? '' : 'none';
  }

  /* ── Gallery: Swiper init + variant switching ── */

  function initGallerySwiper(section) {
    if (typeof Swiper === 'undefined') return;
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      var swiperEl = el.querySelector('.productVisualSwiper');
      if (!swiperEl) return;

      var instance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        autoHeight: true,
        pagination: {
          el: swiperEl.querySelector('.swiper-pagination'),
          clickable: true,
        },
        on: {
          slideChange: function () {
            var thumbs = el.querySelectorAll('.productVisualThumbsSlide');
            thumbs.forEach(function (t) { t.classList.remove('active'); });
            if (thumbs[this.realIndex]) thumbs[this.realIndex].classList.add('active');
          }
        }
      });

      // Thumbnail click → slide to that index
      el.querySelectorAll('.productVisualThumbsSlide').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          instance.slideTo(parseInt(this.dataset.index, 10) - 1);
        });
      });
    });
  }

  function switchGallery(section, id) {
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      el.classList.toggle('active', el.dataset.id === id);
    });
  }

  function parseProductJSON(section) {
    var el = section.querySelector('.productV5JSON');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ── Variant resolution ── */

  /**
   * Given the selected flavor value (string) and an optional audience
   * option value, find the best-matching variant from product data.
   */
  function resolveVariant(productData, flavorValue, audienceValue) {
    if (!productData || !productData.variants) return null;
    var variants = productData.variants;

    // Try: audience option match + flavor match
    if (audienceValue) {
      var match = variants.find(function (v) {
        return v.options.indexOf(audienceValue) !== -1 &&
               v.options.indexOf(flavorValue) !== -1;
      });
      if (match) return match;
    }

    // Fall back: flavor match only (first variant with that flavor value)
    return variants.find(function (v) {
      return v.options.indexOf(flavorValue) !== -1;
    }) || null;
  }

  /**
   * Apply a resolved variant to the section: update prices,
   * hidden input, and availability state.
   */
  function applyVariant(section, variant, productData) {
    if (!variant) return;

    // ── Gallery: show images for this variant ──
    switchGallery(section, String(variant.id));

    // ── Variant input (for cart) ──
    var variantInput = section.querySelector('.productV5VariantInput');
    if (variantInput) variantInput.value = variant.id;

    // ── Prices: calculate dynamically from buy group percent (mirrors v2 updateProduct) ──
    var activePlan = section.querySelector('.productV5GroupsItemInput:checked');
    var percent    = activePlan ? (parseInt(activePlan.dataset.percent, 10) || 0) : 0;
    var quantity   = activePlan ? (parseInt(activePlan.dataset.quantity, 10) || 1) : 1;

    // OTP is always a single month, so it never multiplies by the active
    // plan's quantity. Only used as a fallback when otp_price text is empty.
    var otpPrice   = variant.price;
    var subPrice   = Math.round(variant.price * (1 - percent / 100)) * quantity;
    var cmpPrice   = variant.compare_at_price;
    var isSub      = section.querySelector('.productV5PurchaseRadio[value="subscription"]:checked') !== null;
    var displayPrice = isSub ? subPrice : otpPrice;

    // Main price display. Amount comes from monthly; cadence uses the editable
    // sub_price_per setting with monthly's suffix as fallback.
    var monthlyParts = (isSub && activePlan && activePlan.dataset.monthly)
      ? activePlan.dataset.monthly.split('/')
      : null;
    var mainPriceEl = section.querySelector('.productV5Price');
    if (mainPriceEl) {
      mainPriceEl.textContent = monthlyParts
        ? monthlyParts[0].trim()
        : fmtMoney(displayPrice);
    }
    var pricePerEl = section.querySelector('.productV5PricePer');
    if (pricePerEl) {
      var perText = activePlan && activePlan.dataset.subPricePer
        ? activePlan.dataset.subPricePer.trim()
        : (monthlyParts && monthlyParts[1] ? monthlyParts[1].trim() : '');
      if (perText) {
        pricePerEl.textContent = '/' + perText;
        pricePerEl.style.display = '';
      } else {
        pricePerEl.style.display = 'none';
      }
    }

    // Compare-at price
    var comparePriceEl = section.querySelector('.productV5ComparePrice');
    if (comparePriceEl) {
      if (cmpPrice && cmpPrice > variant.price) {
        comparePriceEl.textContent = fmtMoney(cmpPrice);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Sub row price — left side from buy_group's "Monthly price text" (split on "/"),
    // right side overridable per buy_group via the "Subscription cadence label" setting
    // (inline_richtext, exposed as data-sub-price-per). Falls back to the suffix from
    // the monthly text when the override is empty.
    var subPriceEl = section.querySelector('.productV5SubPrice');
    if (subPriceEl) {
      if (activePlan && activePlan.dataset.monthly) {
        var subParts = activePlan.dataset.monthly.split('/');
        var subAmount = subParts[0].trim();
        var subPerFallback = subParts[1] ? subParts[1].trim() : '';
        var subPerOverride = (activePlan.dataset.subPricePer || '').trim();
        var subPer = subPerOverride || subPerFallback;
        subPriceEl.textContent = subAmount;
        if (subPer) subPriceEl.insertAdjacentHTML('beforeend', '/' + subPer);
      } else {
        subPriceEl.textContent = fmtMoney(subPrice);
      }
    }

    // Sub box header discount badge — reflect selected plan's percent
    var discountEl = section.querySelector('.productV5SubBoxHeaderDiscount');
    if (discountEl && activePlan && activePlan.dataset.percent) {
      discountEl.textContent = 'SAVE ' + activePlan.dataset.percent + '%';
    }

    // OTP price — always from the 1-month buy group's otp_price text, never the
    // active plan, so switching to "3 Month Supply" leaves the OTP card at $99.
    var otpPlan = getOtpPlan(section);
    var otpPriceEl = section.querySelector('.productV5OtpPrice');
    if (otpPriceEl) {
      otpPriceEl.textContent = (otpPlan && otpPlan.dataset.otpPrice)
        ? otpPlan.dataset.otpPrice
        : fmtMoney(otpPrice);
    }

    // ATC button price
    var atcPriceEl = section.querySelector('.productV5SubAtcPrice');
    if (atcPriceEl) {
      atcPriceEl.textContent = activePlan && activePlan.dataset.firstMonthPrice
        ? activePlan.dataset.firstMonthPrice
        : fmtMoney(subPrice);
    }

    // ATC availability
    var atcBtn = section.querySelector('.productV5Atc');
    if (atcBtn) {
      var available = variant.available !== false;
      atcBtn.disabled = !available;
      var atcTextEl = atcBtn.querySelector('.productV5AtcText');
      if (atcTextEl && !available) {
        setAtcButtonText(section, 'Sold Out', '', false);
      }
    }

    // Update variant JSON cache + URL
    var variantJsonEl = section.querySelector('.productV5VariantJSON');
    if (variantJsonEl) variantJsonEl.textContent = JSON.stringify(variant);
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // ── Selling plan availability: disable plans the variant doesn't support ──
    var availablePlanIds = (variant.selling_plan_allocations || []).map(function (a) {
      return String(a.selling_plan_id);
    });
    if (availablePlanIds.length > 0) {
      var needsReselect = false;
      section.querySelectorAll('.productV5GroupsItemInput').forEach(function (input) {
        var isAvailable = availablePlanIds.indexOf(input.value) !== -1;
        input.disabled = !isAvailable;
        var item = input.closest('.productV5GroupsItem');
        if (item) item.classList.toggle('is-disabled', !isAvailable);
        if (!isAvailable && input.checked) {
          input.checked = false;
          needsReselect = true;
        }
      });
      if (needsReselect) {
        var firstAvailable = section.querySelector('.productV5GroupsItemInput:not([disabled])');
        if (firstAvailable) {
          firstAvailable.checked = true;
          firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Plan-derived texts (servings, delivery, badge, save badge) — keep in sync
    applyPlanTexts(section);
  }

  /* ── Apply plan text fields (no variant needed) ── */

  function applyPlanTexts(section) {
    var activePlan = section.querySelector('.productV5GroupsItemInput:checked');
    if (!activePlan) return;

    var monthlyParts = splitPlanText(activePlan.dataset.monthly);
    var monthlyAmount = monthlyParts[0] || '';
    var monthlyCadence = activePlan.dataset.subPricePer || monthlyParts[1] || '';
    var firstMonthPrice = activePlan.dataset.firstMonthPrice || monthlyAmount;
    var saveBadgeText = activePlan.dataset.saveBadgeText || (activePlan.dataset.percent ? 'SAVE ' + activePlan.dataset.percent + '%' : '');
    // OTP text fields come from the 1-month buy group, not the active plan.
    var otpPlan = getOtpPlan(section);
    var otpPriceText = otpPlan ? (otpPlan.dataset.otpPrice || '') : '';
    var isSub = section.querySelector('.productV5PurchaseRadio[value="subscription"]:checked') !== null;
    var subscriptionDisplayPrice = firstMonthPrice;
    var subscriptionDisplayCadence = monthlyCadence || '';
    var subscriptionCtaPrice = subscriptionDisplayPrice + (subscriptionDisplayCadence ? '/' + subscriptionDisplayCadence : '');

    setText(section, '.productV5SubPrice', firstMonthPrice);
    setText(section, '.productV5SubscriberPrice', firstMonthPrice);
    var atcBtn = section.querySelector('.productV5Atc');
    var isAvailable = !atcBtn || !atcBtn.disabled;
    setAtcButtonText(section, isSub ? 'Subscribe' : 'Add to Cart', isSub ? subscriptionCtaPrice : otpPriceText, isAvailable);

    // The welcome kit always describes the subscription offer, so its cadence
    // follows the active plan only — never the purchase mode. Hiding it in OTP
    // made "$179/month" visibly shrink to "$179" when toggling One-Time Purchase.
    var subscriberPricePer = section.querySelector('.productV5SubscriberPricePer');
    if (subscriberPricePer) {
      if (subscriptionDisplayCadence) {
        subscriberPricePer.textContent = '/' + subscriptionDisplayCadence;
        subscriberPricePer.style.display = '';
      } else {
        subscriberPricePer.style.display = 'none';
      }
    }

    // Save badge
    var saveBadge = section.querySelector('.productV5SaveBadge');
    if (saveBadge && saveBadgeText) {
      saveBadge.textContent = saveBadgeText;
    }

    var subComparePrice = section.querySelector('.productV5SubComparePrice');
    if (subComparePrice && monthlyAmount) {
      subComparePrice.textContent = monthlyAmount;
    }

    // Welcome-kit total value = blend compare price + every gift item's fixed
    // compare price. Recomputed on every plan change since the blend's compare
    // price varies by plan (1 Month vs 3 Month, etc). The welcome kit no longer
    // renders the blend's own row (gifts-only redesign), so monthlyAmount — the
    // active plan's "Monthly price text" setting — is used directly instead of
    // reading a DOM node that used to hold the same value.
    var totalValueEl = section.querySelector('.productV5TotalValue');
    if (totalValueEl) {
      var blendCompareCents = parseMoneyToCents(monthlyAmount);
      var giftCompareCents = 0;
      section.querySelectorAll('.productV5KitItemGift .productV5KitComparePrice').forEach(function (el) {
        giftCompareCents += parseMoneyToCents(el.textContent);
      });
      totalValueEl.textContent = fmtMoney(blendCompareCents + giftCompareCents);
    }

    // OTP price from the 1-month buy group's otp_price text field
    var otpPriceEl = section.querySelector('.productV5OtpPrice');
    if (otpPriceEl && otpPriceText) {
      otpPriceEl.textContent = otpPriceText;
    }

    // Supply text also stays pinned to the 1-month plan ("4-Week Supply"),
    // so it never flips to "12-Week supply" when 3 Month is highlighted.
    var otpSupplyText = otpPlan
      ? (otpPlan.dataset.otpSupplyText || otpPlan.dataset.deliveredOtp || '')
      : '';
    setText(section, '.productV5OtpSupply', otpSupplyText);
    setText(section, '.productV5SubscriptionSupply', activePlan.dataset.subscriptionSupplyText || '');
    setText(section, '.productV5BilledToday', activePlan.dataset.billedTodayText || '');
    setText(section, '.productV5RecurringBilling', activePlan.dataset.recurringBillingText || '');
    setText(section, '.productV5SubServings', activePlan.dataset.servingsDescription || '');

    // Main top price display. Product 5 can hide this entirely via section setting,
    // but when visible it follows the active purchase type.
    var mainPriceEl = section.querySelector('.productV5Price');
    if (mainPriceEl) {
      mainPriceEl.textContent = isSub ? firstMonthPrice : otpPriceText;
    }
    var pricePerEl = section.querySelector('.productV5PricePer');
    if (pricePerEl) {
      if (isSub && monthlyCadence) {
        pricePerEl.textContent = '/' + monthlyCadence;
        pricePerEl.style.display = '';
      } else {
        pricePerEl.style.display = 'none';
      }
    }

    var atcPriceEl = section.querySelector('.productV5SubAtcPrice');
    if (atcPriceEl) atcPriceEl.textContent = isSub ? firstMonthPrice : otpPriceText;

    // Price badge — "As low as $X/serving a day" — derived from active plan's servings_per
    var badgeEl = section.querySelector('.productV5PriceBadge');
    if (badgeEl && activePlan.dataset.servingsPer) {
      badgeEl.textContent = 'As low as ' + activePlan.dataset.servingsPer + ' a day';
    }
  }

  /* ── Update from current selections ── */

  function updateFromSelections(section) {
    var productData = parseProductJSON(section);
    if (!productData) return;

    var flavorCard = section.querySelector('.productV5FlavorCard.is-selected');
    var flavorValue = flavorCard ? (flavorCard.dataset.flavorValue || '') : '';

    // Variety pack: no single variant — update plan text fields only
    if (flavorValue === 'variety') {
      applyPlanTexts(section);
      return;
    }

    var variant;

    if (flavorCard) {
      // Flavor picker is present — resolve variant from card + audience
      var audienceInput = section.querySelector('.productV5AudienceInput:checked');
      var audienceValue = audienceInput ? (audienceInput.dataset.optionValue || '') : '';

      // Use data-variant-id from card directly when no audience filter applies
      var directVariantId = parseInt(flavorCard.dataset.variantId, 10);
      if (directVariantId && !audienceValue) {
        variant = productData.variants.find(function (v) { return v.id === directVariantId; });
      }
      if (!variant) {
        variant = resolveVariant(productData, flavorValue, audienceValue);
      }
    } else {
      // No flavor picker — resolve from the hidden variant input, or first available
      var variantInput = section.querySelector('.productV5VariantInput');
      var currentId = variantInput ? parseInt(variantInput.value, 10) : null;
      if (currentId) {
        variant = productData.variants.find(function (v) { return v.id === currentId; });
      }
      if (!variant) {
        variant = productData.variants.find(function (v) { return v.available; }) || productData.variants[0];
      }
    }

    applyVariant(section, variant, productData);
  }

  /* ── Audience selector ── */

  function initAudienceSelector(section) {
    var inputs = section.querySelectorAll('.productV5AudienceInput');
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        section.querySelectorAll('.productV5AudienceItem').forEach(function (item) {
          item.classList.remove('is-selected');
        });
        var label = input.closest('.productV5AudienceItem');
        if (label) label.classList.add('is-selected');
        updateFromSelections(section);
      });
    });
  }

  /* ── Variety pack: restrict buy group plans ── */

  function applyVarietyPlanGating(section, varietyPlanId) {
    var inputs = section.querySelectorAll('.productV5GroupsItemInput');
    if (!varietyPlanId) return;

    // ── Gallery: show variety pack images ──
    switchGallery(section, 'variety');

    var needsReselect = false;
    inputs.forEach(function (input) {
      var isAllowed = input.value === varietyPlanId;
      input.disabled = !isAllowed;
      var item = input.closest('.productV5GroupsItem');
      if (item) item.classList.toggle('is-disabled', !isAllowed);
      if (!isAllowed && input.checked) {
        input.checked = false;
        needsReselect = true;
      }
    });

    if (needsReselect) {
      var allowed = section.querySelector('.productV5GroupsItemInput[value="' + varietyPlanId + '"]');
      if (allowed) {
        allowed.checked = true;
        allowed.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      // Families plan was already selected — no change event fires, update texts directly
      applyPlanTexts(section);
    }
  }

  function clearVarietyPlanGating(section) {
    section.querySelectorAll('.productV5GroupsItemInput').forEach(function (input) {
      input.disabled = false;
      var item = input.closest('.productV5GroupsItem');
      if (item) item.classList.remove('is-disabled');
    });
  }

  /* ── Flavor selector (variant-based) ── */

  function initFlavorSelector(section) {
    var cards = section.querySelectorAll('.productV5FlavorCard');
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        cards.forEach(function (c) { c.classList.remove('is-selected'); });
        card.classList.add('is-selected');

        if (card.dataset.flavorValue === 'variety') {
          applyVarietyPlanGating(section, card.dataset.varietyPlan || '');
        } else {
          clearVarietyPlanGating(section);
          updateFromSelections(section);
        }
      });
    });
  }

  /* ── Sub / OTP purchase toggle ── */

  function initPurchaseToggle(section) {
    var radios     = section.querySelectorAll('.productV5PurchaseRadio');
    var subDetails = section.querySelector('.productV5SubDetails');
    var atcBtn     = section.querySelector('.productV5Atc');

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        // Row highlight
        section.querySelectorAll('.productV5PurchaseRow').forEach(function (row) {
          row.classList.remove('is-selected');
        });
        var row = radio.closest('.productV5PurchaseRow');
        if (row) row.classList.add('is-selected');

        // Only one radio dot may read as selected at a time. The plan input
        // stays checked (it defines which plan resumes when the shopper goes
        // back to subscription), so the sub box is flagged instead and CSS
        // dims the plan dot while One-Time Purchase is active.
        var subBox = section.querySelector('.productV5SubBox');
        if (subBox) subBox.classList.toggle('is-otp-active', radio.value === 'otp');

        // Recalculate prices for the toggled purchase type. There is a single
        // ATC price element (.productV5SubAtcPrice) reused for both modes —
        // applyPlanTexts rewrites its content, so nothing is hidden here.
        updateFromSelections(section);
      });
    });
  }

  /* ── FAQ accordion ── */

  function initFaqAccordion(section) {
    var btns = section.querySelectorAll('.productV5FaqBtn');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isActive = btn.classList.contains('is-active');
        btns.forEach(function (b) { b.classList.remove('is-active'); });
        if (!isActive) btn.classList.add('is-active');
      });
    });
  }

  /* ── Nutritional label dialog ── */

  function initLabelDialog(section) {
    var openBtn = section.querySelector('.productV5LabelBtn');
    var dialog  = section.querySelector('.productV5LabelDialog');
    if (!openBtn || !dialog) return;

    // Init Swiper inside the dialog
    var labelSwiper = null;
    if (typeof Swiper !== 'undefined') {
      var swiperEl = dialog.querySelector('.productV5LabelSwiper');
      if (swiperEl) {
        labelSwiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          autoHeight: true,
          navigation: {
            prevEl: dialog.querySelector('.productV5LabelNavBtn.swiper-button-prev'),
            nextEl: dialog.querySelector('.productV5LabelNavBtn.swiper-button-next'),
          },
        });
      }
    }

    // Open: jump to the slide matching the active variant
    openBtn.addEventListener('click', function () {
      if (labelSwiper) {
        var variantInput = section.querySelector('.productV5VariantInput');
        var currentId    = variantInput ? variantInput.value : null;
        if (currentId) {
          var slides = Array.from(dialog.querySelectorAll('.swiper-slide[data-id]'));
          var idx    = slides.findIndex(function (s) { return s.dataset.id === String(currentId); });
          if (idx >= 0) labelSwiper.slideTo(idx, 0);
        }
      }
      document.body.classList.add('noScroll');
      dialog.showModal();
    });

    var closeBtn = dialog.querySelector('.productV5LabelDialogClose');
    if (closeBtn) closeBtn.addEventListener('click', function () {
      dialog.close();
      document.body.classList.remove('noScroll');
    });

    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        dialog.close();
        document.body.classList.remove('noScroll');
      }
    });

    dialog.addEventListener('close', function () {
      document.body.classList.remove('noScroll');
    });
  }

  /* ── Buy group selector ── */

  function initBuyGroupSelector(section) {
    // The plan rows are the visible UI for "subscription mode" — picking one
    // must also re-select the hidden subscription radio so initPurchaseToggle's
    // OTP/subscription toggle (row highlight, ATC price swap) stays in sync,
    // in case the shopper had switched to One-Time Purchase.
    function selectPlan(section) {
      var subRadio = section.querySelector('.productV5PurchaseRadio[value="subscription"]');
      if (subRadio && !subRadio.checked) {
        subRadio.checked = true;
        subRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // applyPlanTexts (called via updateFromSelections → applyVariant) handles
      // all plan-derived text updates (servings, delivery, badge, save badge).
      updateFromSelections(section);
    }

    section.querySelectorAll('.productV5GroupsItemInput').forEach(function (input) {
      input.addEventListener('change', function () {
        selectPlan(section);
      });

      // A plan row that is ALREADY checked fires no change event when clicked,
      // so while One-Time Purchase is active, clicking the highlighted plan
      // would leave the shopper stuck in OTP. Click covers that case; when the
      // click does change the selection, change fires first and this is a no-op.
      input.addEventListener('click', function () {
        selectPlan(section);
      });
    });
  }

  /* ── ATC button ── */

  function initAtcButton(section, productData) {
    var atcBtn = section.querySelector('.productV5AtcSub');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', async function () {
      this.classList.add('loading');

      var hasSub     = section.querySelector('.productV5PurchaseRadio[value="subscription"]:checked') !== null;
      var activePlan = section.querySelector('.productV5GroupsItemInput:checked');
      var planId     = activePlan ? (parseInt(activePlan.value) || null) : null;
      var subPlanId  = hasSub ? planId : null;
      // OTP is always a single unit. Without this, a shopper who highlights
      // "3 Month Supply" and then picks One-Time Purchase gets charged for 3
      // units at full price while the button only quotes the 1-month price.
      var quantity   = hasSub
        ? (activePlan ? (parseInt(activePlan.dataset.quantity) || 1) : 1)
        : 1;

      var variantInput = section.querySelector('.productV5VariantInput');
      var variantId    = variantInput ? parseInt(variantInput.value) : null;

      var flavorCard = section.querySelector('.productV5FlavorCard.is-selected');
      var isVariety  = flavorCard && flavorCard.dataset.flavorValue === 'variety';

      var formData = [];

      // Gifts (uses giftsV5 global set in Liquid)
      if (typeof giftsV5 !== 'undefined') {
        giftsV5.forEach(function (gift) {
          var giftId = parseInt(gift.id);
          if (!giftId) return;
          if (hasSub || !gift.sub) {
            formData.push({ quantity: 1, id: giftId, properties: { _gift: true } });
          }
        });
      }

      if (isVariety) {
        // Variety: add each real flavor variant individually (mirrors v2)
        section.querySelectorAll('.productV5FlavorCard:not(.productV5FlavorCardVariety)').forEach(function (card) {
          var vid = parseInt(card.dataset.variantId);
          if (!vid) return;
          formData.push({
            quantity: 1,
            id: vid,
            selling_plan: subPlanId,
            properties: { _plan: planId, _variety: true }
          });
        });
      } else {
        formData.push({
          quantity: quantity,
          id: variantId,
          selling_plan: subPlanId,
          // _plan mirrors selling_plan: an OTP line has no plan, so reporting
          // shouldn't record the subscription plan that happened to be highlighted.
          properties: { _plan: subPlanId }
        });
      }

      await fetch('/cart/clear.js');
      await addProduct({ items: formData });

      // 1-click checkout: skip the drawer entirely. The loading class stays on
      // deliberately — clearing it would flash the button back to its resting
      // state while the browser is already navigating away.
      if (section.dataset && section.dataset.atcCheckout === 'true') {
        window.location.href = '/checkout';
        return;
      }

      this.classList.remove('loading');
      await openCart();
    });
  }

  /* ── Gift / Learn more dialogs ── */

  function initGiftDialogs(section) {
    section.querySelectorAll('.productV5SubPointLearnBtn').forEach(function (btn) {
      var item   = btn.closest('.productV5SubPointItem');
      var dialog = item ? item.querySelector('.productV5SubPointLearnDialog') : null;
      if (!dialog) return;

      btn.addEventListener('click', function () { dialog.showModal(); });

      dialog.querySelectorAll('.productV5SubPointLearnClose').forEach(function (cb) {
        cb.addEventListener('click', function () { dialog.close(); });
      });

      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) dialog.close();
      });
    });
  }

  /* ── Theme editor support ── */

  document.addEventListener('shopify:section:load', function (e) {
    if (e.detail && e.detail.sectionId === sectionId) init();
  });

  /* ── Boot ── */

  function init() {
    var section = getSection();
    if (!section) return;

    var productData = parseProductJSON(section);

    initGallerySwiper(section);
    initAudienceSelector(section);
    initFlavorSelector(section);
    initPurchaseToggle(section);
    initBuyGroupSelector(section);
    initAtcButton(section, productData);
    initFaqAccordion(section);
    initLabelDialog(section);
    initGiftDialogs(section);

    // Sync prices on initial load from the first selected flavor
    var initialVarietyCard = section.querySelector('.productV5FlavorCard.is-selected.productV5FlavorCardVariety');
    if (initialVarietyCard) {
      applyVarietyPlanGating(section, initialVarietyCard.dataset.varietyPlan || '');
    } else {
      updateFromSelections(section);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
